import Cookies, { CookieAttributes } from 'js-cookie';
import { ICookieExpiryOptions } from '../types';

function _getCookieExpiry(expiry?: ICookieExpiryOptions): Date | undefined {
	if (!expiry) return;
	const { days = 0, minutes = 0, hours = 0 } = expiry;
	let expiresInMillisecons = minutes;
	if (hours) {
		expiresInMillisecons += hours * 60;
	}
	if (days) {
		expiresInMillisecons += days * 60 * 24;
	}
	expiresInMillisecons = expiresInMillisecons * 60 * 1000;
	return new Date(new Date().getTime() + expiresInMillisecons);
}

/**
 * @description In order to set a cookie we can use this function.
 *
 * @param {string} cookieName
 * @param {string} cookieValue
 * @param {object} options
 *
 * valid keys for the options is:
 * @param {string}  [path="/"]  - A String indicating the path where the cookie is visible.
 * @param {object}  [expires={minutes: 0, hours: 0, days: 0}]  - Define when the cookie will be removed. Value must be
 * object with keys: minutes, hours and days. If not provided the cookie will becomes a session cookie.
 * @param {string}  [domain=''] - A String indicating a valid domain where the cookie should be visible. The cookie will also be visible to all subdomains.
 * @param {boolean} [secure=false]  - Either true or false, indicating if the cookie transmission requires a secure protocol (https).
 * @param {string}  [sameSite=''] - A String, allowing to control whether the browser is sending a cookie along
 * with cross-site requests. Valid values are lax, strict, none.
 *
 */
export function setCookie(
	cookieName: string,
	cookieValue: string,
	options: Omit<CookieAttributes, 'expires'> & {
		expires?: ICookieExpiryOptions;
	} = {},
) {
	return Cookies.set(cookieName, cookieValue, {
		...options,
		expires: _getCookieExpiry(options.expires),
	});
}

/**
 * @description This function is to get the single cookie value. This will return the value of cookie if it is presented
 * otherwise it will return empty value
 *
 * @param {string} cookieName
 * @returns {string}
 */
export function getCookie(cookieName: string) {
	return Cookies.get(cookieName) || '';
}

/**
 * @description This function is to get all the cookies that is presented for the domain. Returns object.
 *
 * @returns {object}
 */
export function getAllCookies() {
	return Cookies.get() || {};
}

/**
 * @description This function is to remove a given cookie.
 *
 * IMPORTANT! When deleting a cookie and you're not relying on the default attributes, you must pass the exact
 * same path and domain attributes that were used to set the cookie.
 *
 * @param {string} cookieName
 * @param {CookieAttributes} options
 *
 * Valid keys for the options are:
 * @param {string}  [path="/"]  - A String indicating the path where the cookie is visible.
 * @param {string}  [domain=""] - A String indicating a valid domain where the cookie should be visible.
 */
export function deleteCookie(cookieName: string, options?: CookieAttributes) {
	return Cookies.remove(cookieName, options);
}
